import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbQrcode, TbRefresh, TbDownload, TbCode, TbCheck } from "react-icons/tb";
import { apiRequest } from "src/services/apiClient";
import { config } from "configs/config";
import { SettingRow, Section, TextInput } from "./SettingsControls";
import Popup from "src/components/Popup/Popup";

interface PraxisQrData {
    hash: string | null;
    qrDataUrl?: string;
    createdAt?: string;
    embedUrl?: string;
    widgetHeading?: string;
    widgetMessage?: string;
}

type DownloadFormat = "png" | "svg" | "pdf";
type DownloadSize = "a7" | "a6" | "dinLang" | "a5" | "a4" | "a3";
const DOWNLOAD_FORMATS: DownloadFormat[] = ["pdf", "svg", "png"];
const DOWNLOAD_SIZES: DownloadSize[] = ["a7", "a6", "dinLang", "a5", "a4", "a3"];

// Mirrors PRAXIS_QR_SIZES in backend/src/services/praxisRegistrationQrService.ts — needed
// client-side to label the size options.
const DIN_SIZES: Record<DownloadSize, { shortName: string }> = {
    a7:      { shortName: "A7" },
    a6:      { shortName: "A6" },
    dinLang: { shortName: "DIN Lang" },
    a5:      { shortName: "A5" },
    a4:      { shortName: "A4" },
    a3:      { shortName: "A3" },
};

const SIZE_DESC_KEY: Record<DownloadSize, string> = {
    a7: "settings.praxisQrSizeA7Desc",
    a6: "settings.praxisQrSizeA6Desc",
    dinLang: "settings.praxisQrSizeDinLangDesc",
    a5: "settings.praxisQrSizeA5Desc",
    a4: "settings.praxisQrSizeA4Desc",
    a3: "settings.praxisQrSizeA3Desc",
};

// Single source of truth for both the copyable snippet and the preview <iframe> — they'd
// otherwise silently drift apart (the preview showing a different, smaller box than what
// actually gets embedded, so it wouldn't reflect what really fits).
const EMBED_WIDTH = 360;
const EMBED_HEIGHT = 520;

/** Settings → Praxis → persistent, unlimited-use QR code for patients to self-register from
 *  the waiting room. Distinct from CreateRecipientModal's blank QR (single-use, generated
 *  fresh per walk-in) — this one is meant to be generated once and either downloaded/printed
 *  by staff, or embedded directly on the praxis's own website via RegisterWidget. */
export function PraxisRegistrationQr({ praxisName }: { praxisName: string }) {
    const { t } = useTranslation();
    const [data, setData] = useState<PraxisQrData | null>(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [confirmRegenerate, setConfirmRegenerate] = useState(false);
    // Baked into SVG/PDF downloads only — not persisted. Always starts from the current
    // praxis name; editing it just changes what gets embedded in a freshly downloaded file.
    const [label, setLabel] = useState(praxisName);
    const [downloadFormat, setDownloadFormat] = useState<DownloadFormat>("pdf");
    const [downloadSize, setDownloadSize] = useState<DownloadSize>("a5");
    const [downloading, setDownloading] = useState(false);
    const [copied, setCopied] = useState(false);
    // Widget copy — persisted (unlike `label` above), so these track the last-saved value
    // rather than resetting to a fixed default every time this section is opened.
    const [heading, setHeading] = useState("");
    const [message, setMessage] = useState("");
    const [savingText, setSavingText] = useState(false);
    const [textSaved, setTextSaved] = useState(false);
    // Bumped after regenerate/save-text so the <iframe> preview actually reloads — its src
    // only changes on regenerate (new hash); a wording-only save leaves the src identical, so
    // the iframe wouldn't otherwise re-fetch on its own.
    const [previewKey, setPreviewKey] = useState(0);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await apiRequest<PraxisQrData>("/patient-portal/questionnaire/praxis-qr");
                if (!cancelled) {
                    setData(res);
                    setHeading(res.widgetHeading ?? "");
                    setMessage(res.widgetMessage ?? "");
                }
            } catch {
                if (!cancelled) setError(t("settings.praxisQrLoadError"));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const regenerate = async () => {
        setGenerating(true);
        setError(null);
        try {
            const res = await apiRequest<PraxisQrData>("/patient-portal/questionnaire/praxis-qr/regenerate", { method: "POST" });
            setData(res);
            setHeading(res.widgetHeading ?? "");
            setMessage(res.widgetMessage ?? "");
            setPreviewKey((k) => k + 1);
        } catch {
            setError(t("settings.praxisQrGenerateError"));
        } finally {
            setGenerating(false);
            setConfirmRegenerate(false);
        }
    };

    const saveWidgetText = async () => {
        setSavingText(true);
        setError(null);
        try {
            const res = await apiRequest<{ widgetHeading: string; widgetMessage: string }>(
                "/patient-portal/questionnaire/praxis-qr/widget-text",
                { method: "PATCH", body: JSON.stringify({ heading, message }) },
                { "Content-Type": "application/json" },
            );
            setData((prev) => (prev ? { ...prev, widgetHeading: res.widgetHeading, widgetMessage: res.widgetMessage } : prev));
            setPreviewKey((k) => k + 1);
            setTextSaved(true);
            setTimeout(() => setTextSaved(false), 2000);
        } catch {
            setError(t("settings.praxisQrWidgetTextError"));
        } finally {
            setSavingText(false);
        }
    };

    // apiRequest always parses the response as JSON, so a binary file needs a raw fetch — same
    // reasoning CreateRecipientModal already applies to its own QR endpoint.
    const download = async () => {
        setDownloading(true);
        setError(null);
        try {
            const token = localStorage.getItem("token");
            const params = new URLSearchParams({ format: downloadFormat, size: downloadSize, label: label.trim() });
            const res = await fetch(`${config.baseUrl}/api/patient-portal/questionnaire/praxis-qr/download?${params}`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!res.ok) throw new Error("Download failed");
            const blob = await res.blob();
            const filename = res.headers.get("Content-Disposition")?.match(/filename="([^"]+)"/)?.[1]
                ?? `praxis-anmeldung-qr.${downloadFormat}`;
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            setError(t("settings.praxisQrDownloadError"));
        } finally {
            setDownloading(false);
        }
    };

    const embedSnippet = data?.embedUrl
        ? `<iframe src="${data.embedUrl}" width="${EMBED_WIDTH}" height="${EMBED_HEIGHT}" style="border:0;" title="${praxisName} – ${t("settings.praxisQrTitle")}"></iframe>`
        : "";

    const copyEmbed = async () => {
        if (!embedSnippet) return;
        try {
            await navigator.clipboard.writeText(embedSnippet);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            setError(t("settings.praxisQrEmbedCopyError"));
        }
    };

    return (
        <Section title={t("settings.praxisQrTitle")} description={t("settings.praxisQrDesc")}>
            <SettingRow title={t("settings.praxisQrRowTitle")} description={t("settings.praxisQrRowDesc")} column>
                {loading ? (
                    <span className="praxis-qr__loading">{t("common.loading")}</span>
                ) : (
                    <div className="praxis-qr">
                        {data?.qrDataUrl ? (
                            <>
                                <img src={data.qrDataUrl} alt={t("settings.praxisQrRowTitle")} className="praxis-qr__image" />
                                <div className="praxis-qr__actions">
                                    <label className="praxis-qr__field">
                                        <span>{t("settings.praxisQrLabelField")}</span>
                                        <TextInput value={label} onChange={setLabel} placeholder={praxisName} />
                                    </label>
                                    <div className="praxis-qr__field">
                                        <span>{t("settings.praxisQrFormatField")}</span>
                                        <div className="toggle-group" role="radiogroup" aria-label={t("settings.praxisQrFormatField")}>
                                            {DOWNLOAD_FORMATS.map((f) => (
                                                <button
                                                    key={f}
                                                    type="button"
                                                    role="radio"
                                                    aria-checked={downloadFormat === f}
                                                    className={`toggle-btn${downloadFormat === f ? " toggle-btn--active" : ""}`}
                                                    onClick={() => setDownloadFormat(f)}
                                                >
                                                    {f.toUpperCase()}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <label className="praxis-qr__field">
                                        <span>{t("settings.praxisQrSizeField")}</span>
                                        <select
                                            className="settings-select"
                                            value={downloadSize}
                                            onChange={(e) => setDownloadSize(e.target.value as DownloadSize)}
                                        >
                                            {DOWNLOAD_SIZES.map((s) => (
                                                <option key={s} value={s}>
                                                    {DIN_SIZES[s].shortName} · {t(SIZE_DESC_KEY[s])}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    <div className="praxis-qr__buttons">
                                        <button type="button" className="btn-outline" onClick={download} disabled={downloading}>
                                            <TbDownload /> {downloading ? t("settings.praxisQrDownloading") : t("settings.praxisQrDownloadBtn")}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn-outline"
                                            onClick={() => setConfirmRegenerate(true)}
                                            disabled={generating}
                                        >
                                            <TbRefresh /> {t("settings.praxisQrRegenerate")}
                                        </button>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <button type="button" className="btn-outline" onClick={regenerate} disabled={generating}>
                                <TbQrcode /> {generating ? t("settings.praxisQrGenerating") : t("settings.praxisQrGenerate")}
                            </button>
                        )}
                        {error && <p className="praxis-qr__error">{error}</p>}
                    </div>
                )}
            </SettingRow>

            {data?.qrDataUrl && (
                <>
                    <SettingRow title={t("settings.praxisQrWidgetTextField")} description={t("settings.praxisQrWidgetTextDesc")} column>
                        <div className="praxis-qr__widget-text">
                            <label className="praxis-qr__field">
                                <span>{t("settings.praxisQrWidgetHeadingField")}</span>
                                <TextInput value={heading} onChange={setHeading} placeholder={t("registerWidgetDefaults.heading")} />
                            </label>
                            <label className="praxis-qr__field">
                                <span>{t("settings.praxisQrWidgetMessageField")}</span>
                                <TextInput value={message} onChange={setMessage} placeholder={t("registerWidgetDefaults.message")} />
                            </label>
                            <button type="button" className="btn-outline" onClick={saveWidgetText} disabled={savingText}>
                                {textSaved ? <><TbCheck /> {t("settings.praxisQrWidgetTextSaved")}</> : (savingText ? t("settings.praxisQrWidgetTextSaving") : t("settings.praxisQrWidgetTextSave"))}
                            </button>
                        </div>
                    </SettingRow>

                    <SettingRow title={t("settings.praxisQrEmbedField")} description={t("settings.praxisQrEmbedDesc")} column>
                        <div className="praxis-qr__embed">
                            <iframe
                                key={previewKey}
                                src={data.embedUrl}
                                title={t("settings.praxisQrEmbedPreviewTitle")}
                                className="praxis-qr__embed-preview"
                                width={EMBED_WIDTH}
                                height={EMBED_HEIGHT}
                            />
                            <div className="praxis-qr__embed-code-wrap">
                                <textarea
                                    readOnly
                                    value={embedSnippet}
                                    className="praxis-qr__embed-code"
                                    onClick={(e) => (e.target as HTMLTextAreaElement).select()}
                                />
                                <button type="button" className="btn-outline" onClick={copyEmbed}>
                                    {copied ? <><TbCheck /> {t("settings.praxisQrEmbedCopied")}</> : <><TbCode /> {t("settings.praxisQrEmbedCopy")}</>}
                                </button>
                            </div>
                        </div>
                    </SettingRow>
                </>
            )}

            {confirmRegenerate && (
                <Popup
                    title={t("settings.praxisQrRegenerate")}
                    message={t("settings.praxisQrRegenerateConfirm")}
                    onClose={() => setConfirmRegenerate(false)}
                    actions={[
                        { label: t("popup.cancel"), variant: "default", disabled: generating, onClick: () => setConfirmRegenerate(false) },
                        { label: t("settings.praxisQrRegenerate"), variant: "danger", disabled: generating, onClick: regenerate },
                    ]}
                />
            )}
        </Section>
    );
}
